/**
 * ChargeInvoicesModal — as faturas de um charge, com as ações de cobrança.
 *
 * É o drill-in da tela Cobrança — Títulos. A churn de um charge (cancelar e regerar, quebrar em
 * frações) só faz sentido vista aqui, junto.
 *
 * TODAS as ações ficam no kebab da linha — um lugar só para decidir, em vez de botões soltos
 * competindo com o menu.
 *
 * Conta e Meio vêm de `billing.payment` (a fatura não guarda mais meio/conta desde o split da
 * AF-3321): só existem para fatura paga. Meio mostra BOLETO já na emissão — é o sinal de que a
 * fatura tem boleto, papel que antes era de um ícone de código de barras.
 *
 * As ações ofertadas seguem o CENÁRIO do charge, e não é enfeite: oferecer o modo errado gera
 * movimentação errada no settlement.
 *   RECEIVABLE  → Nova Fatura, Liquidar, Baixa Parcial, Cancelar, Deixar de Cobrar
 *   INSTALLMENT → idem + **Amortizar Operação** (baldinho: o dinheiro entra na operação e é
 *                 distribuído pelas parcelas, em vez de liquidar esta)
 *   GUARANTEE   → Nova Fatura, Liquidar, Baixa Parcial, Cancelar, Deixar de Cobrar — igual a
 *                 RECEIVABLE desde que a garantia passou a ter TÍTULO próprio. O que ela não
 *                 oferece é Amortizar Operação: o rateio nas parcelas acontece nos dois casos,
 *                 automaticamente, e oferecê-lo sugeriria existir pagamento de garantia que NÃO
 *                 amortiza a operação
 *
 * O backend é a autoridade: cada fatura vem com `chargeScenario` e `allowsPartialPayment`, e as
 * mesmas regras são revalidadas lá (o menu escondido não é a proteção).
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import MenuButton, { MenuOption } from '../../../atoms/MenuButton';
import BadgeStatus from '../../../atoms/BadgeStatus';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { collectInvoice, listInvoicesByCharge, uncollectInvoice } from '../services/billingApi';
import { CHARGE_SCENARIO_LABELS } from '../constants/chargeFilterSchema';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import MarkInvoicePaymentModal, { PaymentMode } from './MarkInvoicePaymentModal';
import UnmarkSignalModal from './UnmarkSignalModal';
import NewInvoiceModal from './NewInvoiceModal';
import CancelInvoiceModal from './CancelInvoiceModal';
import BoletoModal from './BoletoModal';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise';

// Paleta de cobrança (ver settlement/constants/settlement.ts): pagamento é verde, o tom diz qual.
const statusColors: Record<string, BadgeColor> = {
    OPEN: 'gold', PARTIALLY_PAID: 'olive', PAID: 'green', OVERPAID: 'teal', CANCELLED: 'red',
};

const MENU = {
    NEW: 'new-invoice',
    SETTLE: 'settle',
    PARTIAL: 'partial',
    POST_SETTLEMENT: 'post-settlement',
    AMORTIZE: 'amortize',
    CANCEL: 'cancel',
    // uma entrada só para as duas ações: elas são inversas e nunca cabem juntas —
    // oferecer 'Deixar de Cobrar' desabilitado numa fatura já fora da cobrança dizia
    // ao usuário o que ele NÃO pode fazer, em vez do que ele pode
    UNCOLLECT: 'uncollect',
    COLLECT: 'collect',
    // toma o lugar das opções de pagamento na fatura JÁ sinalizada: ali não há o que pagar de
    // novo, e o que a cobrança precisa é poder voltar atrás
    UNMARK: 'unmark',
    BOLETO: 'boleto',
    DETAILS: 'details',
} as const;

/** Fatura em aberto é a única que aceita ação de cobrança. */
const isOpen = (inv: any) => inv.status === 'OPEN' || inv.status === 'PARTIALLY_PAID';

/** Aberta com vencimento no passado. O backend guarda OPEN; vencida é leitura de data. */
const isOverdue = (inv: any) => (
    inv.status === 'OPEN' && !!inv.dueDate && inv.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10)
);

/**
 * Meio de pagamento. Boleto emitido aparece como BOLETO mesmo antes de pago — é o sinal de que
 * existe boleto (substituiu o ícone de código de barras). Sem boleto e sem pagamento: "-".
 */
const paymentMethodLabel = (inv: any): string => {
    if (inv.boleto) return 'BOLETO';
    return inv.payment?.method || inv.payment?.type || '-';
};

/** Cabeçalhos: valores à direita, estados ao centro. */
/**
 * A fração paga do charge, em aritmética EXATA (num/den inteiros).
 *
 * ⚠️ Não dá para somar 1/d em float: 1/3 + 1/3 + 1/3 = 0.9999999999999998 e o último pagamento de
 * um charge quebrado em três nunca fecharia. Mesma razão pela qual o backend usa `fractions.
 * Fraction` (billing/src/domain/charge_fraction.py) e a view arredonda em 6 casas.
 */
const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

const sumFractions = (denominators: number[]) => denominators.reduce(
    (acc, d) => {
        const den = d > 0 ? d : 1;
        const num = acc.num * den + acc.den;      // acc + 1/den
        const newDenominator = acc.den * den;
        const g = gcd(num, newDenominator) || 1;
        return { num: num / g, den: newDenominator / g };
    },
    { num: 0, den: 1 },
);

/** Denominador da fração que a fatura cobra do charge (ausente = cobra o charge inteiro). */
const denominatorOf = (inv: any) => Number(inv.details?.totalInstallments) || 1;

/**
 * "Liquidar" só é oferecido quando ESTA fatura fecha o charge: a fração dela mais o que já foi
 * pago alcança 1. Uma 1/1 (original ou derivada) sempre fecha; uma 1/2 só depois que a irmã foi
 * paga; uma 1/3 com 1/2 pago, nunca.
 *
 * A regra vive na TELA de propósito: no backend a baixa é IMPERATIVA (o usuário está no comando,
 * e "liquidar" é ação, não conclusão sobre valor). Sem esta restrição, oferecer "Liquidar" numa
 * fração isolada deixaria o usuário quitar o título com dinheiro faltando sem perceber.
 */
export const canSettleCharge = (invoice: any, todasDoCharge: any[]) => {
    const paidOnes = todasDoCharge.filter(i => i.status === 'PAID').map(denominatorOf);
    const { num, den } = sumFractions([...paidOnes, denominatorOf(invoice)]);
    return num >= den;
};

const ALIGN: Record<string, string> = {
    Valor: 'text-right', Pago: 'text-right',
    Status: 'text-center', Conta: 'text-center', Meio: 'text-center',
    Cobrada: 'text-center', Fração: 'text-center', 'Ações': 'text-center',
};

interface Props {
    charge: any;
    onClose: () => void;
    onChanged: () => void;
}

const ChargeInvoicesModal: React.FC<Props> = ({ charge, onClose, onChanged }) => {
    const [invoices, setInvoices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [detailsInvoice, setDetailsInvoice] = useState<any>(null);
    const [boletoInvoice, setBoletoInvoice] = useState<any>(null);
    const [paying, setPaying] = useState<
        { invoice: any; mode: PaymentMode; postSettlement?: boolean } | null>(null);
    const [deriving, setDeriving] = useState<any>(null);
    const [cancelling, setCancelling] = useState<any>(null);
    const [unmarking, setUnmarking] = useState<any>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listInvoicesByCharge(charge.creditOperationId, charge.chargeId);
            setInvoices(data.list || data.items || []);
        } catch (e: any) {
            toast.error(e?.message || 'Falha ao carregar as faturas');
        } finally {
            setLoading(false);
        }
    }, [charge.creditOperationId, charge.chargeId]);

    useEffect(() => { load(); }, [load]);

    const scenario: string = charge.chargeScenario || 'GUARANTEE';
    const isGuarantee = scenario === 'GUARANTEE';
    // Charge sem título é dado legado (garantia de antes de ela virar título): sem título não há a
    // que amarrar cobrança nova nem a que aplicar baixa, e o backend recusa as duas.
    const withoutTitle = !charge.titleDebtId;
    // baldinho DECLARADO só em parcela: na garantia o rateio já acontece sempre
    const allowsWaterfall = scenario === 'INSTALLMENT';
    /**
     * Título já liquidado. Aqui não existe "liquidar" (não há dívida) nem "baixa parcial" (nada a
     * manter aberto): qualquer dinheiro que chegue é recebimento a maior. Oferecer os dois modos
     * levava o usuário a escolher entre duas ações que o backend trataria igual — e "Liquidar"
     * ainda sugeriria que faltava liquidar algo.
     */
    const titleSettled = charge.titleDebtStatus === 'PAID' || charge.titleDebtStatus === 'OVERPAID';

    /**
     * A fatura deste título que já tem pagamento sinalizado, se houver.
     *
     * Um título só pode ter UMA marcação pendente: sem isso dois pagamentos disputariam o mesmo
     * título, a primeira aprovação pagaria a fatura e a segunda encontraria fatura já paga
     * apontando para outro pagamento — o mesmo dinheiro contado duas vezes. O servidor recusa; a
     * tela desabilita para o usuário não descobrir isso só no fim.
     */
    const signalledInvoice = invoices.find((i: any) => i.paymentSignalled);

    const menuOptions = (inv: any): MenuOption[] => {
        const invoiceIsOpen = isOpen(inv);
        const isSignalled = !!inv.paymentSignalled;
        const titleHasMarking = !!signalledInvoice;
        /**
         * Duas regras que se completam:
         *
         * - na fatura MARCADA as opções de pagamento não APARECEM: não há o que pagar, o dinheiro
         *   já está apontado para ela;
         * - nas IRMÃS elas aparecem DESABILITADAS, com o motivo. Esconder ali diria que a ação não
         *   existe, quando o que houve é o título estar ocupado.
         *
         * Como a primeira já remove as opções da própria marcada, quem vê a opção desabilitada é
         * sempre uma irmã — é isso que autoriza a mensagem a falar do "outro pagamento".
         */
        const paymentOptionsVisible = !isSignalled;
        const TITLE_TAKEN_HINT = 'Para marcar o pagamento desta fatura, finalize o outro pagamento '
            + 'pendente neste título';
        // `collect` ausente em dado antigo conta como EM cobrança: é o default da coluna,
        // e tratar ausência como "fora da cobrança" ofereceria 'Cobrar Fatura' para
        // uma fatura que já está sendo cobrada
        const isCollecting = inv.collect !== false;
        const allowsPartial = inv.allowsPartialPayment !== false && !withoutTitle;
        // fração isolada não liquida o título: a opção só aparece quando esta fatura fecha o charge
        const closesTheCharge = canSettleCharge(inv, invoices);
        return [
            {
                key: MENU.NEW, label: 'Nova Fatura', type: 'item', backgroundColor: '#2563eb',
                disabled: withoutTitle,
                title: withoutTitle
                    ? 'Cobrança sem título no settlement: não há dívida registrada a que amarrar uma fatura nova'
                    : 'Gerar fatura(s) a partir desta',
            },
            { key: 'sep-1', type: 'separator' },
            {
                key: MENU.SETTLE, label: 'Liquidar', type: 'item', backgroundColor: '#059669',
                visible: !titleSettled && paymentOptionsVisible,
                disabled: !invoiceIsOpen || !closesTheCharge || titleHasMarking,
                title: titleHasMarking ? TITLE_TAKEN_HINT
                    : !invoiceIsOpen ? 'Só para fatura em aberto'
                    : !closesTheCharge ? `Esta fatura cobra 1/${denominatorOf(inv)} do charge: com o que já `
                        + 'foi pago, ela não fecha a dívida. Use Baixa Parcial — liquidar aqui '
                        + 'quitaria o título com valor faltando.'
                    : 'Sinalizar o pagamento que liquida o título',
            },
            {
                // mesma cor do estado que a ação produz (olive = pagamento parcial)
                key: MENU.PARTIAL, label: 'Baixa Parcial', type: 'item', backgroundColor: '#808000',
                visible: !titleSettled && paymentOptionsVisible,
                disabled: !invoiceIsOpen || !allowsPartial || titleHasMarking,
                title: titleHasMarking ? TITLE_TAKEN_HINT
                    : !allowsPartial
                    ? 'Sem título no settlement não há a que aplicar baixa parcial'
                    : 'Sinalizar o pagamento que mantém o título aberto',
            },
            {
                // teal = recebimento a maior, a mesma cor do status OVERPAID nas listas: o usuário
                // reconhece o que vai acontecer antes de abrir o modal
                key: MENU.POST_SETTLEMENT, label: 'Pagto pós Liq.', type: 'item',
                backgroundColor: '#008080',
                visible: titleSettled && paymentOptionsVisible,
                disabled: !invoiceIsOpen || titleHasMarking,
                title: titleHasMarking ? TITLE_TAKEN_HINT
                    : !invoiceIsOpen ? 'Só para fatura em aberto'
                    : 'O título já está liquidado: o valor entra como recebimento a maior',
            },
            {
                // baldinho: só faz sentido em parcela, onde é a garantia da operação que paga
                key: MENU.AMORTIZE, label: 'Amortizar Operação', type: 'item',
                backgroundColor: '#7c3aed',
                visible: allowsWaterfall && paymentOptionsVisible,
                disabled: !invoiceIsOpen || titleHasMarking,
                title: titleHasMarking ? TITLE_TAKEN_HINT
                    : 'O valor entra na operação e é distribuído pelas parcelas, em vez de liquidar esta',
            },
            {
                // Só na fatura marcada, e é ali a única forma de voltar atrás. Âmbar porque é a
                // cor de "aguardando" nas telas de pagamento — desfazer a espera, não destruir.
                key: MENU.UNMARK, label: 'Remover Sinalização', type: 'item',
                backgroundColor: '#d97706',
                visible: isSignalled,
                title: 'Desfaz a marcação deste pagamento, informando o motivo',
            },
            { key: 'sep-2', type: 'separator' },
            isCollecting
                ? {
                    key: MENU.UNCOLLECT, label: 'Deixar de Cobrar', type: 'item',
                    backgroundColor: '#64748b',
                    disabled: !invoiceIsOpen,
                    title: 'Para de cobrar sem cancelar — a fatura continua devida',
                }
                : {
                    // verde-cinza: retomar a cobrança é construtivo, ao contrário do par ao lado
                    key: MENU.COLLECT, label: 'Cobrar Fatura', type: 'item',
                    backgroundColor: '#0f766e',
                    disabled: !invoiceIsOpen,
                    title: !invoiceIsOpen
                        ? 'Só fatura em aberto volta a ser cobrada'
                        : 'Volta a cobrar esta fatura, que estava fora da cobrança',
                },
            {
                key: MENU.CANCEL, label: 'Cancelar Fatura', type: 'item', backgroundColor: '#b91c1c',
                disabled: inv.status === 'PAID' || inv.status === 'CANCELLED',
                title: inv.status === 'PAID' ? 'Fatura paga não é cancelada' : 'Cancelar a cobrança',
            },
            { key: 'sep-3', type: 'separator' },
            {
                key: MENU.BOLETO, label: 'Boleto', type: 'item', backgroundColor: '#2563eb',
                disabled: !inv.boleto,
                title: inv.boleto ? 'Dados e download do boleto' : 'Esta fatura não tem boleto',
            },
            { key: MENU.DETAILS, label: 'Detalhes', type: 'item', backgroundColor: '#475569' },
        ];
    };

    const handleSelect = (inv: any) => async (option: string) => {
        if (option === MENU.DETAILS) return setDetailsInvoice(inv);
        if (option === MENU.BOLETO) return setBoletoInvoice(inv);
        if (option === MENU.CANCEL) return setCancelling(inv);
        if (option === MENU.UNMARK) return setUnmarking(inv);
        if (option === MENU.UNCOLLECT || option === MENU.COLLECT) {
            const retomar = option === MENU.COLLECT;
            try {
                await (retomar ? collectInvoice(inv.invoiceId) : uncollectInvoice(inv.invoiceId));
                toast.success(retomar ? 'A fatura voltou a ser cobrada'
                                      : 'A fatura deixou de ser cobrada');
                await load();
                onChanged();
            } catch (e: any) {
                toast.error(e?.message || (retomar ? 'Falha ao voltar a cobrar'
                                                   : 'Falha ao deixar de cobrar'));
            }
            return;
        }
        if (option === MENU.SETTLE) return setPaying({ invoice: inv, mode: 'TOTAL' });
        if (option === MENU.PARTIAL) return setPaying({ invoice: inv, mode: 'PARTIAL' });
        if (option === MENU.AMORTIZE) return setPaying({ invoice: inv, mode: 'OPERATION_WATERFALL' });
        // PARTIAL porque não há título a liquidar; a cara própria é do postSettlement
        if (option === MENU.POST_SETTLEMENT) {
            return setPaying({ invoice: inv, mode: 'PARTIAL', postSettlement: true });
        }
        if (option === MENU.NEW) return setDeriving(inv);
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">
                                Faturas — {charge.contractLabel || charge.contractNumber || 'charge'}
                            </h3>
                            {/* Ordem padrão das telas de cobrança: identificador, natureza,
                                Título/NF, Nº e valor. O charge encurtado abre com "#", como nas
                                tabelas — escrito "charge 3f21c620" ele parecia um rótulo de campo
                                em vez do id que é. */}
                            <p className="text-xs text-gray-500 mt-0.5">
                                #{String(charge.chargeId).slice(0, 8)}
                                {' · '}{CHARGE_SCENARIO_LABELS[scenario] || scenario}
                                {' · '}Título/NF {charge.documentTitle || '-'}
                                {charge.installmentNumber != null && <> · Nº {charge.installmentNumber}</>}
                                {charge.amount != null && <> · valor {formatBRL(charge.amount)}</>}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    {isGuarantee && !withoutTitle && (
                        <div className="mx-6 mt-3 px-3 py-2 text-xs bg-slate-100 dark:bg-slate-700 rounded">
                            Recebível de <strong>garantia</strong>: liquidar ou baixar parcialmente decide o
                            efeito no título de garantia. O valor é <strong>também</strong> distribuído pelas
                            parcelas da operação, nos dois casos — por isso não há "Amortizar Operação" aqui.
                        </div>
                    )}
                    {withoutTitle && (
                        <div className="mx-6 mt-3 px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/30 rounded">
                            Cobrança <strong>sem título</strong> no settlement (dado anterior à garantia virar
                            título): nova fatura e baixa ficam indisponíveis, porque não há dívida registrada
                            a que amarrá-las.
                        </div>
                    )}

                    {/* min-h: o menu da linha é posicionado dentro deste container com overflow,
                        então sem altura mínima as opções ficavam cortadas quando há poucas linhas */}
                    <div className="flex-1 overflow-auto px-6 py-4 min-h-[400px]">
                        {loading ? (
                            <p className="text-sm text-gray-500">Carregando…</p>
                        ) : invoices.length === 0 ? (
                            <p className="text-sm text-gray-500">Nenhuma fatura neste charge.</p>
                        ) : (
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-100 dark:bg-slate-700">
                                    <tr>
                                        {['Fatura', 'Criação', 'Valor', 'Pago', 'Vencimento',
                                            'Status', 'Conta', 'Meio', 'Cobrada', 'Fração', 'Ações'].map(h => (
                                                <th key={h}
                                                    className={`px-3 py-2 font-medium ${ALIGN[h] || 'text-left'}`}>
                                                    {h}
                                                </th>
                                            ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv: any) => (
                                        <tr key={inv.invoiceId}
                                            className="border-b dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700">
                                            <td className="px-3 py-2 font-mono text-xs" title={inv.invoiceId}>
                                                {String(inv.invoiceId).slice(0, 8)}
                                                {inv.details?.parentInvoiceId && (
                                                    <span className="ml-1 text-[10px] text-gray-400"
                                                        title={`Deriva de ${inv.details.parentInvoiceId}`}>↳</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">{inv.createdAt ? formatDateBR(inv.createdAt) : '-'}</td>
                                            <td className="px-3 py-2 text-right">{formatBRL(inv.amount)}</td>
                                            <td className="px-3 py-2 text-right">
                                                {inv.amountPaid != null ? formatBRL(inv.amountPaid) : '-'}
                                            </td>
                                            <td className={`px-3 py-2 ${isOverdue(inv) ? 'text-red-600 font-medium' : ''}`}>
                                                {inv.dueDate ? formatDateBR(inv.dueDate) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {/* Vencida NÃO troca o selo: o status continua "Em
                                                    aberto" e o atraso é dito pela DATA em vermelho,
                                                    igual à tela de Faturas.

                                                    Trocar o selo custava informação: "Vencida"
                                                    ocupava o lugar de "Pagto Sinalizado" numa
                                                    fatura vencida que já tem pagamento apontado —
                                                    exatamente a que mais precisa ser reconhecida,
                                                    porque cobrá-la seria cobrar de quem já pagou. */}
                                                <BadgeStatus text={inv.statusFormatted || inv.status}
                                                    color={statusColors[inv.status] || 'gray'} />
                                            </td>
                                            {/* conta e meio vivem em billing.payment: só existem para fatura paga */}
                                            <td className="px-3 py-2 text-center text-xs"
                                                title={inv.payment?.accountName || undefined}>
                                                {inv.payment?.accountAlias || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center text-xs">
                                                {paymentMethodLabel(inv)}
                                            </td>
                                            <td className="px-3 py-2 text-center whitespace-nowrap">
                                                {inv.collect
                                                    ? <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">SIM</span>
                                                    : <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded font-medium">NÃO</span>}
                                            </td>
                                            {/* fração do charge que esta fatura cobra */}
                                            <td className="px-3 py-2 text-center text-xs text-gray-500">
                                                {inv.details?.installmentNumber ?? 1}/{inv.details?.totalInstallments ?? 1}
                                            </td>
                                            {/* todas as ações no kebab: boleto e detalhes viraram itens do menu */}
                                            <td className="px-3 py-2 text-center whitespace-nowrap">
                                                <MenuButton
                                                    options={menuOptions(inv)}
                                                    onSelect={handleSelect(inv)}
                                                    ariaLabel="Ações da fatura"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end">
                        <button onClick={onClose}
                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                            Fechar
                        </button>
                    </div>
                </div>
            </div>

            {detailsInvoice && (
                <InvoiceDetailsModal invoice={detailsInvoice} onClose={() => setDetailsInvoice(null)} />
            )}
            {paying && (
                <MarkInvoicePaymentModal
                    invoice={paying.invoice}
                    charge={charge}
                    mode={paying.mode}
                    postSettlement={paying.postSettlement}
                    onClose={() => setPaying(null)}
                    onSuccess={() => { load(); onChanged(); }}
                />
            )}
            {deriving && (
                <NewInvoiceModal
                    invoice={deriving}
                    charge={charge}
                    onClose={() => setDeriving(null)}
                    onSuccess={() => { load(); onChanged(); }}
                />
            )}
            {unmarking && (
                <UnmarkSignalModal
                    invoice={unmarking}
                    /* `details.paidBy` e NÃO `paidBy`: o serializer da fatura põe o campo
                       dentro de `details` (é lá que estão os identificadores). Lendo do topo vinha
                       undefined e o dialog chamava a rota sem pagamento — mesmo tipo de erro que
                       já mandou a coluna Título/NF vazia para produção.

                       A contagem do lote não vem daqui de propósito: `linkedInvoicesCount` é campo
                       do PAGAMENTO, não da fatura, e o lote pode atravessar títulos que esta lista
                       nem carregou. Quem conta é o próprio dialog. */
                    paymentId={unmarking.details?.paidBy || unmarking.paidBy}
                    onClose={() => setUnmarking(null)}
                    onSuccess={() => { load(); onChanged(); }}
                />
            )}
            {cancelling && (
                <CancelInvoiceModal
                    invoice={cancelling}
                    onClose={() => setCancelling(null)}
                    onSuccess={() => { load(); onChanged(); }}
                />
            )}
            {boletoInvoice && (
                <BoletoModal invoice={boletoInvoice} onClose={() => setBoletoInvoice(null)}
                             onChanged={load} />
            )}
        </>
    );
};

export default ChargeInvoicesModal;
